import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import {CalendarDays, ArrowLeft, Loader2, Phone, Mail, Building2, Clock, Filter, CheckCircle2, XCircle, CalendarCheck, Eye, X, MessageSquare, Save, User, } from 'lucide-react'
import toast from "react-hot-toast"

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  nouveau: { label: "Nouveau", color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20", icon: CalendarDays },
  confirme: { label: "Confirmé", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", icon: CalendarCheck },
  termine: { label: "Terminé", color: "text-slate-400 bg-slate-500/10 border-slate-500/20", icon: CheckCircle2 },
  annule: { label: "Annulé", color: "text-red-400 bg-red-500/10 border-red-500/20", icon: XCircle },
}

const typeColors: Record<string, string> = {
  "Appel découverte": "text-blue-400 bg-blue-500/10",
  "Audit IT": "text-purple-400 bg-purple-500/10",
  "Rendez-vous technique": "text-amber-400 bg-amber-500/10",
  "Assistance planifiée": "text-emerald-400 bg-emerald-500/10",
}

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>("all")
  const [filterType, setFilterType] = useState<string>("all")
  const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null)
  const [notes, setNotes] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadAppointments()
  }, [])

  const loadAppointments = async () => {
    try {
      const { list } = await lumi.entities.appointments.list({
        sort: { createdAt: -1 },
        limit: 200,
      })
      setAppointments(list)
    } catch (err) {
      console.error("Erreur chargement rendez-vous:", err)
      toast.error("Erreur lors du chargement des rendez-vous")
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      setSaving(true)
      await lumi.entities.appointments.update(id, {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      })
      setAppointments((prev) =>
        prev.map((a) => (a._id === id ? { ...a, status: newStatus, updatedAt: new Date().toISOString() } : a))
      )
      if (selectedAppointment?._id === id) {
        setSelectedAppointment((prev: any) => prev ? { ...prev, status: newStatus } : null)
      }
      toast.success(`Statut mis à jour : ${statusConfig[newStatus]?.label || newStatus}`)
    } catch (err) {
      console.error("Erreur mise à jour:", err)
      toast.error("Erreur lors de la mise à jour du statut")
    } finally {
      setSaving(false)
    }
  }

  const saveNotes = async (id: string) => {
    try {
      setSaving(true)
      await lumi.entities.appointments.update(id, {
        notes,
        updatedAt: new Date().toISOString(),
      })
      setAppointments((prev) =>
        prev.map((a) => (a._id === id ? { ...a, notes, updatedAt: new Date().toISOString() } : a))
      )
      if (selectedAppointment?._id === id) {
        setSelectedAppointment((prev: any) => prev ? { ...prev, notes } : null)
      }
      toast.success("Notes enregistrées")
    } catch (err) {
      console.error("Erreur sauvegarde notes:", err)
      toast.error("Erreur lors de la sauvegarde des notes")
    } finally {
      setSaving(false)
    }
  }

  const filtered = appointments.filter((a) => {
    if (filterStatus !== "all" && a.status !== filterStatus) return false
    if (filterType !== "all" && a.type !== filterType) return false
    return true
  })

  const counts = {
    total: appointments.length,
    nouveau: appointments.filter((a) => a.status === "nouveau").length,
    confirme: appointments.filter((a) => a.status === "confirme").length,
    termine: appointments.filter((a) => a.status === "termine").length,
    annule: appointments.filter((a) => a.status === "annule").length,
  }

  const openDetail = (appointment: any) => {
    setSelectedAppointment(appointment)
    setNotes(appointment.notes || "")
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—"
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    } catch {
      return dateStr
    }
  }

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "—"
    try {
      return new Date(dateStr).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20">
      <div className="section-padding">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 animate-fade-in-up">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-3">
              <ArrowLeft className="w-4 h-4" />
              Retour au tableau de bord
            </Link>
            <h1 className="text-2xl lg:text-3xl font-bold text-white">
              Gestion des <span className="gradient-text">rendez-vous</span>
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {counts.total} rendez-vous au total &middot; {counts.nouveau} nouveau{counts.nouveau > 1 ? "x" : ""}
            </p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8 animate-fade-in-up" style={{ animationDelay: "0.05s" }}>
          {[
            { label: "Total", value: counts.total, color: "from-cyan-500 to-blue-500", icon: CalendarDays },
            { label: "Nouveaux", value: counts.nouveau, color: "from-amber-500 to-orange-500", icon: Clock },
            { label: "Confirmés", value: counts.confirme, color: "from-emerald-500 to-teal-500", icon: CalendarCheck },
            { label: "Terminés", value: counts.termine, color: "from-slate-500 to-slate-600", icon: CheckCircle2 },
            { label: "Annulés", value: counts.annule, color: "from-red-500 to-pink-500", icon: XCircle },
          ].map((stat, i) => (
            <div key={stat.label} className="card !p-4" style={{ animationDelay: `${0.05 * i}s` }}>
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-2`}>
                <stat.icon className="w-4 h-4 text-white" />
              </div>
              <div className="text-xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-slate-400">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400">Filtrer :</span>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field !py-1.5 !px-3 text-sm !w-auto"
          >
            <option value="all">Tous les statuts</option>
            <option value="nouveau">Nouveau</option>
            <option value="confirme">Confirmé</option>
            <option value="termine">Terminé</option>
            <option value="annule">Annulé</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input-field !py-1.5 !px-3 text-sm !w-auto"
          >
            <option value="all">Tous les types</option>
            <option value="Appel découverte">Appel découverte</option>
            <option value="Audit IT">Audit IT</option>
            <option value="Rendez-vous technique">Rendez-vous technique</option>
            <option value="Assistance planifiée">Assistance planifiée</option>
          </select>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card !p-12 text-center animate-fade-in-up">
            <CalendarDays className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Aucun rendez-vous</h3>
            <p className="text-slate-400 text-sm">
              {filterStatus !== "all" || filterType !== "all"
                ? "Aucun rendez-vous ne correspond aux filtres sélectionnés."
                : "Aucune demande de rendez-vous pour le moment."}
            </p>
          </div>
        ) : (
          /* Appointments List */
          <div className="space-y-3 animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
            {filtered.map((appointment) => {
              const sc = statusConfig[appointment.status] || statusConfig.nouveau
              return (
                <div
                  key={appointment._id}
                  className="card !p-4 lg:!p-5 hover:border-cyan-500/30 transition-all cursor-pointer group"
                  onClick={() => openDetail(appointment)}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${sc.color}`}>
                          <sc.icon className="w-3 h-3" />
                          {sc.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[appointment.type] || "text-slate-400 bg-slate-500/10"}`}>
                          {appointment.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium text-white">{appointment.nom}</span>
                        {appointment.entreprise && (
                          <span className="text-xs text-slate-500">
                            <Building2 className="w-3 h-3 inline mr-0.5" />
                            {appointment.entreprise}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {appointment.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {appointment.telephone}
                        </span>
                      </div>
                    </div>

                    {/* Date & time */}
                    <div className="flex items-center gap-4 lg:gap-6">
                      <div className="text-right">
                        <div className="flex items-center gap-1.5 text-sm font-medium text-white">
                          <CalendarDays className="w-4 h-4 text-cyan-400" />
                          {formatDate(appointment.datePreferee)}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5 justify-end">
                          <Clock className="w-3 h-3" />
                          {appointment.heurePreferee || "Non précisé"}
                        </div>
                      </div>
                      <Eye className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors shrink-0" />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Detail Modal */}
        {selectedAppointment && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedAppointment(null)}>
            <div
              className="bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
                <div>
                  <h2 className="text-lg font-bold text-white">Détail du rendez-vous</h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Créé le {formatDateTime(selectedAppointment.createdAt)}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedAppointment(null)}
                  className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-6">
                {/* Status + Actions */}
                <div>
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2 block">Statut</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusConfig).map(([key, cfg]) => (
                      <button
                        key={key}
                        onClick={() => updateStatus(selectedAppointment._id, key)}
                        disabled={saving}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                          selectedAppointment.status === key
                            ? cfg.color + " ring-1 ring-offset-1 ring-offset-slate-900"
                            : "border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300"
                        } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <cfg.icon className="w-3.5 h-3.5" />
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="card !p-4">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1 block">Contact</label>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-white">
                        <User className="w-4 h-4 text-cyan-400" />
                        {selectedAppointment.nom}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Mail className="w-4 h-4 text-slate-500" />
                        <a href={`mailto:${selectedAppointment.email}`} className="hover:text-cyan-400 transition-colors">
                          {selectedAppointment.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Phone className="w-4 h-4 text-slate-500" />
                        <a href={`tel:${selectedAppointment.telephone}`} className="hover:text-cyan-400 transition-colors">
                          {selectedAppointment.telephone}
                        </a>
                      </div>
                      {selectedAppointment.entreprise && (
                        <div className="flex items-center gap-2 text-sm text-slate-300">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          {selectedAppointment.entreprise}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="card !p-4">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1 block">Rendez-vous</label>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-white">
                        <CalendarDays className="w-4 h-4 text-cyan-400" />
                        {formatDate(selectedAppointment.datePreferee)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Clock className="w-4 h-4 text-slate-500" />
                        {selectedAppointment.heurePreferee || "Non précisé"}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[selectedAppointment.type] || "text-slate-400 bg-slate-500/10"}`}>
                          {selectedAppointment.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details */}
                {selectedAppointment.details && (
                  <div>
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2 block">Détails de la demande</label>
                    <div className="card !p-4">
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{selectedAppointment.details}</p>
                    </div>
                  </div>
                )}

                {/* Notes internes */}
                <div>
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" />
                    Notes internes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ajouter des notes internes (visibles uniquement par l'équipe)..."
                    rows={3}
                    className="input-field text-sm resize-none"
                  />
                  <button
                    onClick={() => saveNotes(selectedAppointment._id)}
                    disabled={saving || notes === (selectedAppointment.notes || "")}
                    className="btn-primary text-sm !py-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Enregistrer les notes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
